import { useState } from "react";

import { Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { RefreshPageButton } from "components/RefreshPageButton";
import { ClientAppEvent } from "constants/index";
import {
  useCarrierQuoteFilterHandlers,
  useCarrierQuotes,
  useResettablePage,
  useRoleContext,
} from "hooks";
import { DashboardItemLayout } from "layouts";

import { getPageCount } from "../../../../utils";
import { QuoteFilters } from "../QuoteFilters";
import { QuoteFiltersMorePanel } from "../QuoteFiltersMorePanel";
import { CarrierQuoteList } from "./CarrierQuoteList";

interface Props {
  morePanelOpen?: boolean;
  onMoreToggle?: VoidFunction;
}

const CARRIER_QUOTES_PAGE_SIZE = 10;

export function CarrierQuoteListContainer(props: Props) {
  const {
    morePanelOpen: controlledMorePanelOpen,
    onMoreToggle: controlledOnMoreToggle,
  } = props;
  const { role } = useRoleContext();
  const [internalMorePanelOpen, setInternalMorePanelOpen] = useState(false);
  const posthog = usePostHog();

  const isControlled = controlledMorePanelOpen !== undefined;
  const morePanelOpen = isControlled
    ? controlledMorePanelOpen
    : internalMorePanelOpen;
  const onMoreToggle = () => {
    posthog?.capture(ClientAppEvent.CARRIER_MY_QUOTES_MORE_FILTERS_TOGGLED, {
      opened: !morePanelOpen,
      surface: "dashboard",
    });
    if (isControlled) {
      (controlledOnMoreToggle as VoidFunction)();
    } else {
      setInternalMorePanelOpen((prev) => !prev);
    }
  };

  const {
    filters,
    filterClearCount,
    handleShipmentNameChange,
    handleQuoteStatusChange,
    handleSortByChange,
    handleEquipmentTypesChange,
    handleServiceTypesChange,
    handleShipmentTypeChange,
    handleOriginProvincesChange,
    handleDestinationProvincesChange,
    handlePickupDateAfterChange,
    handlePickupDateBeforeChange,
    handleDeliveryDateAfterChange,
    handleDeliveryDateBeforeChange,
    handleShipmentStatusChange,
    handleClearFilters,
  } = useCarrierQuoteFilterHandlers({ surface: "dashboard" });

  const [page, setPage] = useResettablePage(filters);

  const { quotes, loading, error, count, refetchQuotes } = useCarrierQuotes({
    currentPage: page,
    pageSize: CARRIER_QUOTES_PAGE_SIZE,
    filters,
  });

  const pages = getPageCount(count ?? 0, CARRIER_QUOTES_PAGE_SIZE);

  if (role?.type !== "CARRIER") {
    return null;
  }

  const onPageSelected = (newPageNumber: number) => {
    setPage(newPageNumber);
  };

  return (
    <DashboardItemLayout
      sx={{ width: "100%", p: 2 }}
      title={
        <Stack
          direction="row"
          spacing={4}
          alignItems="baseline"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">My Quotes</Typography>
          <RefreshPageButton
            id="carrier-my-quotes-refresh"
            iconButton
            onRefresh={refetchQuotes}
          />
        </Stack>
      }
    >
      <Stack rowGap={2}>
        <QuoteFilters
          filters={filters}
          onShipmentNameChange={handleShipmentNameChange}
          onQuoteStatusChange={handleQuoteStatusChange}
          onSortByChange={handleSortByChange}
          morePanelOpen={morePanelOpen}
          onMoreToggle={onMoreToggle}
          filterClearCount={filterClearCount}
        />
        {morePanelOpen && (
          <QuoteFiltersMorePanel
            filters={filters}
            onEquipmentTypesChange={handleEquipmentTypesChange}
            onServiceTypesChange={handleServiceTypesChange}
            onShipmentTypeChange={handleShipmentTypeChange}
            onOriginProvincesChange={handleOriginProvincesChange}
            onDestinationProvincesChange={handleDestinationProvincesChange}
            onPickupDateAfterChange={handlePickupDateAfterChange}
            onPickupDateBeforeChange={handlePickupDateBeforeChange}
            onDeliveryDateAfterChange={handleDeliveryDateAfterChange}
            onDeliveryDateBeforeChange={handleDeliveryDateBeforeChange}
            onShipmentStatusChange={handleShipmentStatusChange}
            onClearFilters={handleClearFilters}
          />
        )}
        <CarrierQuoteList
          quotes={quotes}
          loading={loading}
          error={error}
          currentPage={page}
          pageCount={pages}
          onPageSelected={(newPageNumber) => onPageSelected(newPageNumber)}
        />
      </Stack>
    </DashboardItemLayout>
  );
}
